import { useEffect, useState } from "react";
import { CheckCircle, File, FileArchive, FileCode, FileImage, FileText, Loader2, ChevronRight } from "lucide-react";
import "./FileListStreaming.scss";
import { CommonService } from "@/services";
import { ChatMessage } from "@/models/chat.model.ts";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import PublishPopoverComponent from "@/components/common-components/publish-popover/PublishPopoverComponent.tsx";

interface FileListStreamingProps {
    message: ChatMessage;
    projectId?: string;
    appType?: string;
}

const FileListStreaming = ({ message, projectId, appType }: FileListStreamingProps) => {

    //append key as path attribute to each file object
    //keys are the paths but convert them into list of objects taking key as path attribute
    const streamedFiles = Object.keys(message?.extra?.files || {})
        .map((path: any) => path.split('/').pop())

    const [showTick, setShowTick] = useState(false);
    const [showFinalMessage, setShowFinalMessage] = useState(false);
    const [isStreamingEnded, setIsStreamingEnded] = useState(false);

    useEffect(() => {
        const sub = CommonService._communications.endCodeStreamerSubject.subscribe((value: boolean) => {
            if (value) {
                setIsStreamingEnded(true);
            }
        });
        return () => sub.unsubscribe();
    }, []);

    // Calculate current file and completion status
    const currentFile = streamedFiles.length > 0 ? streamedFiles[streamedFiles.length - 1] : "";
    const completedFiles = streamedFiles.length;

    // Calculate if advanced mode is completed
    const processItems = message?.extra?.processItems as any[] | undefined;
    const isAdvancedModeCompleted = processItems && processItems.length > 0 && processItems[processItems.length - 1]?.is_completed;

    useEffect(() => {
        // Show final message if:
        // 1. Standard mode streaming ended
        // 2. OR Advanced mode process is explicitly completed
        // 3. AND message role is no longer "loading" (safety check)
        if ((isStreamingEnded || isAdvancedModeCompleted || message?.role !== "loading")) {
            setShowTick(true);
            setShowTick(false);
            setShowFinalMessage(true);
        } else {
            setShowTick(false);
            setShowFinalMessage(false);
        }
    }, [isStreamingEnded, isAdvancedModeCompleted, message?.role]);

    const getFileIcon = (filename: string) => {
        if (!filename) return <File className="h-6 w-6" />;

        const ext = filename.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'svg':
                return <FileImage className="h-6 w-6" />;
            case 'js':
            case 'ts':
            case 'tsx':
            case 'jsx':
            case 'html':
            case 'css':
            case 'json':
                return <FileCode className="h-6 w-6" />;
            case 'zip':
            case 'rar':
            case 'tar':
            case 'gz':
                return <FileArchive className="h-6 w-6" />;
            case 'txt':
            case 'doc':
            case 'docx':
                return <FileText className="h-6 w-6" />;
            default:
                return <File className="h-6 w-6" />;
        }
    };

    const isMobile = useIsMobile();

    // If in advanced mode and not completed, hide this component to avoid duplicate loading UI
    // (AdvancedModeProcessLoaderComponent handles the loading state)
    if (processItems && processItems.length > 0 && !isAdvancedModeCompleted) {
        return null;
    }

    if (showFinalMessage) {
        return (
            <div className="preview-action-card-wrapper mt-4">
                <div
                    className="preview-action-card group cursor-pointer relative"
                    onClick={() => {
                        if (isMobile) {
                            CommonService._communications.setPreviewLoadingStateSubject.next(true);
                        } else {
                            CommonService._communications.switchCodeAndPreviewSubject.next(false);
                        }
                        CommonService.handleMixpanelTrack("view_preview_card_clicked", {
                            message_id: message.id,
                            device_type: isMobile ? 'mobile' : 'desktop'
                        });
                    }}
                >
                    <div className="card-content">
                        <div className="card-text">
                            <h3 className="card-title">Application Updated</h3>
                            <p className="card-subtitle">Previewing latest version</p>
                        </div>
                        <ChevronRight className="chevron-icon" />
                    </div>

                    {isMobile && projectId && appType && (
                        <PublishPopoverComponent
                            projectId={projectId}
                            appType={appType}
                            showAsTag={true}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`file-list-streaming ${!isStreamingEnded ? 'processing' : ''}`}>
            <div className="file-list-header">
                <span className="file-list-title">File Processing</span>
                <span className={`file-list-chip ${!isStreamingEnded ? 'processing' : 'completed'}`}>
                    {!isStreamingEnded ? 'Processing...' : 'Complete'}
                </span>
            </div>
            <div className="file-list-content">
                <div className="file-item">
                    <div className="file-icon">
                        {!isStreamingEnded ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : showTick ? (
                            <CheckCircle className="h-4 w-4" />
                        ) : (
                            getFileIcon(currentFile || "")
                        )}
                    </div>
                    <span className="file-name">
                        {currentFile || "Processing files..."}
                    </span>
                </div>
                <div className="file-progress">
                    {!isStreamingEnded
                        ? `Processing files (${completedFiles})...`
                        : showTick
                            ? "All files complete"
                            : ""}
                </div>
            </div>
        </div>
    );
};

export default FileListStreaming;
